import { useMemo, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { DATE_CONSTRAINTS, LABELS } from '../constants';
import {
  normalizeSublocationId,
  buildApiSublocationLookup,
  resolveSelectedSublocationEndDateRows,
  deriveSelectionDateFacts,
  deriveMinSelectableStartDate,
  deriveDateConstraintWarnings,
  deriveDateConstraintHint,
  resolveDefaultDateUpdates,
} from './utils/dateConstraintUtils';

const NO_LAST_POSTING_MIN_DATE = dayjs(DATE_CONSTRAINTS.NO_LAST_POSTING_MIN_DATE).startOf('day');
const START_DATE_MIN = dayjs(DATE_CONSTRAINTS.START_DATE_MIN).startOf('day');
const { DISPLAY_DATE_FORMAT } = DATE_CONSTRAINTS;

export const useDateConstraints = ({
  selectedSublocations,
  apiSublocations,
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate,
  onResetState,
}) => {
  const suppressAutoDefaultDatesRef = useRef(false);
  const todayDate = useMemo(() => dayjs().startOf('day'), []);

  const selectedStartDateValue = useMemo(() => {
    if (!selectedStartDate) return null;
    const parsed = dayjs(selectedStartDate).startOf('day');
    return parsed.isValid() ? parsed : null;
  }, [selectedStartDate]);

  const minSelectableEndDate = useMemo(
    () => (selectedStartDateValue ? selectedStartDateValue.startOf('day') : null),
    [selectedStartDateValue],
  );

  const selectedSublocationIds = useMemo(
    () => (selectedSublocations || []).map((id) => normalizeSublocationId(id)).filter(Boolean),
    [selectedSublocations],
  );

  const selectedSublocationIdSet = useMemo(() => new Set(selectedSublocationIds), [selectedSublocationIds]);

  const apiSublocationLookup = useMemo(() => buildApiSublocationLookup(apiSublocations), [apiSublocations]);

  const selectedSublocationEndDateRows = useMemo(
    () => resolveSelectedSublocationEndDateRows(selectedSublocationIds, apiSublocationLookup),
    [selectedSublocationIds, apiSublocationLookup],
  );

  const {
    hasSelectedSublocations,
    hasMixedKnownAndMissingEndDates,
    hasMismatchedKnownEndDates,
    hasNoLastPostingForAll,
    knownEndDateRows,
    maxKnownEndDate,
    effectiveReferenceEndDate,
  } = useMemo(() => deriveSelectionDateFacts(selectedSublocationEndDateRows), [selectedSublocationEndDateRows]);

  const minSelectableStartDate = useMemo(
    () =>
      deriveMinSelectableStartDate({
        effectiveReferenceEndDate,
        hasNoLastPostingForAll,
        startDateMin: START_DATE_MIN,
        noLastPostingMinDate: NO_LAST_POSTING_MIN_DATE,
      }),
    [effectiveReferenceEndDate, hasNoLastPostingForAll],
  );

  const canEditDates = hasSelectedSublocations;
  const selectedSublocationsKey = useMemo(() => selectedSublocationIds.join(','), [selectedSublocationIds]);
  const minSelectableStartDateKey = useMemo(
    () => (minSelectableStartDate ? minSelectableStartDate.format('YYYY-MM-DD') : ''),
    [minSelectableStartDate],
  );

  useEffect(() => {
    suppressAutoDefaultDatesRef.current = false;
  }, [selectedSublocationsKey, minSelectableStartDateKey]);

  const dateConstraintWarnings = useMemo(
    () =>
      deriveDateConstraintWarnings({
        hasSelectedSublocations,
        minSelectableStartDate,
        maxKnownEndDate,
        knownEndDateRows,
        hasMixedKnownAndMissingEndDates,
        selectedSublocationEndDateRows,
        startDateMin: START_DATE_MIN,
        displayDateFormat: DISPLAY_DATE_FORMAT,
      }),
    [
      hasSelectedSublocations,
      minSelectableStartDate,
      maxKnownEndDate,
      knownEndDateRows,
      hasMixedKnownAndMissingEndDates,
      selectedSublocationEndDateRows,
    ],
  );

  const dateConstraintHint = useMemo(
    () =>
      deriveDateConstraintHint({
        hasSelectedSublocations,
        minSelectableStartDate,
        dateConstraintWarnings,
        hasMixedKnownAndMissingEndDates,
        hasMismatchedKnownEndDates,
        mismatchLabel: LABELS.APPLY_DISABLED_DATE_MISMATCH,
      }),
    [
      hasSelectedSublocations,
      minSelectableStartDate,
      dateConstraintWarnings,
      hasMixedKnownAndMissingEndDates,
      hasMismatchedKnownEndDates,
    ],
  );

  // Auto-set default dates when sublocation selection changes
  useEffect(() => {
    if (suppressAutoDefaultDatesRef.current) return;

    const dateUpdates = resolveDefaultDateUpdates({
      hasSelectedSublocations,
      minSelectableStartDate,
      selectedStartDate,
      selectedEndDate,
    });
    if (!dateUpdates) return;

    if (dateUpdates.nextStartDate) {
      setSelectedStartDate(dateUpdates.nextStartDate);
    }
    if (dateUpdates.nextEndDate) {
      setSelectedEndDate(dateUpdates.nextEndDate);
    }
    onResetState?.();
  }, [hasSelectedSublocations, selectedStartDate, selectedEndDate, minSelectableStartDate, onResetState]);

  const handleStartDateChange = useCallback(
    (newDate) => {
      if (!canEditDates) return;

      if (!newDate) {
        suppressAutoDefaultDatesRef.current = true;
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        onResetState?.();
        return;
      }

      const parsed = dayjs(newDate).startOf('day');
      if (!parsed.isValid()) return;
      if (minSelectableStartDate && parsed.isBefore(minSelectableStartDate, 'day')) return;
      if (parsed.isAfter(todayDate, 'day')) return;

      suppressAutoDefaultDatesRef.current = false;
      setSelectedStartDate(parsed.format('YYYY-MM-DD'));
      if (selectedEndDate) {
        const parsedEndDate = dayjs(selectedEndDate).startOf('day');
        if (!parsedEndDate.isValid() || parsedEndDate.isBefore(parsed, 'day')) {
          setSelectedEndDate(null);
        }
      }
      onResetState?.();
    },
    [canEditDates, minSelectableStartDate, todayDate, selectedEndDate, onResetState],
  );

  const handleEndDateChange = useCallback(
    (newDate) => {
      if (!canEditDates) return;

      if (!newDate) {
        suppressAutoDefaultDatesRef.current = true;
        setSelectedEndDate(null);
        onResetState?.();
        return;
      }

      const parsed = dayjs(newDate).startOf('day');
      if (!parsed.isValid()) return;
      if (parsed.isAfter(todayDate, 'day')) return;
      if (selectedStartDateValue && parsed.isBefore(selectedStartDateValue, 'day')) return;

      suppressAutoDefaultDatesRef.current = false;
      setSelectedEndDate(parsed.format('YYYY-MM-DD'));
      onResetState?.();
    },
    [canEditDates, todayDate, selectedStartDateValue, onResetState],
  );

  const shouldDisableStartDate = useCallback(() => false, []);
  const shouldDisableEndDate = useCallback(() => false, []);

  return {
    todayDate,
    minSelectableStartDate,
    minSelectableEndDate,
    canEditDates,
    dateConstraintHint,
    dateConstraintWarnings,
    onStartDateChange: handleStartDateChange,
    onEndDateChange: handleEndDateChange,
    shouldDisableStartDate,
    shouldDisableEndDate,
    hasSelectedSublocations,
    selectedSublocationIdSet,
  };
};
